import { useState, useEffect } from "react";
import { DateField } from "@/components/ui/date-field";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Combobox } from "@/components/ui/combobox";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtEur, fmtDate, photoSrc } from "@/lib/format";
import { Banknote, History, FileText, ExternalLink, Pencil, Check, X, CheckCircle2, RotateCcw } from "lucide-react";

const STATUS = {
  open: { l: "Openstaand", c: "bg-amber-100 text-amber-700" },
  late: { l: "Te laat", c: "bg-red-100 text-red-700" },
  paid: { l: "Betaald / uitbetaald", c: "bg-emerald-100 text-emerald-700" },
};

const Field = ({ label, children, testid }) => (
  <div>
    <div className="text-xs text-slate-400">{label}</div>
    <div className="font-medium text-slate-800" data-testid={testid}>{children}</div>
  </div>
);

export default function FactoringDetailDialog({ open, onOpenChange, factoring, onSaved }) {
  const [f, setF] = useState(factoring);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(factoring || {});
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [collecting, setCollecting] = useState(false);

  useEffect(() => { setF(factoring); setForm(factoring || {}); setEdit(false); }, [factoring, open]);

  if (!f) return null;
  const st = STATUS[f.status] || STATUS.open;
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const editAmount = Number(form.amount) || 0;
  const editRate = Number(form.interest_rate) || 0;
  const editPayout = Math.round((editAmount - editAmount * editRate / 100) * 100) / 100;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...f, ...form, amount: editAmount, interest_rate: editRate,
      };
      const r = await api.put(`/factoring/${f.id}`, payload);
      setF(r.data); setForm(r.data); setEdit(false);
      toast.success("Factuur bijgewerkt");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
    finally { setSaving(false); }
  };

  const togglePayout = async () => {
    setToggling(true);
    try {
      const r = await api.patch(`/factoring/${f.id}/payout`, { done: !f.payout_done });
      setF(r.data); setForm(r.data);
      toast.success(r.data.payout_done ? "Uitbetaling afgevinkt" : "Uitbetaling teruggezet");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Actie mislukt"); }
    finally { setToggling(false); }
  };

  const toggleCollected = async () => {
    setCollecting(true);
    try {
      const r = await api.patch(`/factoring/${f.id}/collected`, { done: !f.collected });
      setF(r.data); setForm(r.data);
      toast.success(r.data.collected ? "Factuurbedrag gemarkeerd als ontvangen" : "Ontvangst teruggezet");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Actie mislukt"); }
    finally { setCollecting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="factoring-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2 pr-8">
            <span className="flex items-center gap-2"><Banknote className="h-5 w-5" /> Factoring-factuur {f.invoice_number || "—"}</span>
            {!edit && <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="factoring-edit-button"><Pencil className="h-4 w-4 mr-1.5" /> Bewerken</Button>}
          </DialogTitle>
          <DialogDescription className="sr-only">Details van de factoring-factuur, incasso en uitbetaling.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
          {!edit ? (
            <>
              <div className="flex items-center gap-3 flex-wrap">
                <Badge className={`${st.c} hover:${st.c}`} data-testid="factoring-detail-status">{st.l}</Badge>
                {f.collected
                  ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Factuurbedrag ontvangen{f.collected_at ? ` op ${fmtDate(f.collected_at)}` : ""}</Badge>
                  : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Factuurbedrag nog niet ontvangen</Badge>}
                {f.payout_done
                  ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Uitbetaald{f.payout_done_at ? ` op ${fmtDate(f.payout_done_at)}` : ""}</Badge>
                  : <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">Uitbetaling openstaand</Badge>}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <Field label="Debiteur" testid="factoring-detail-debtor">{f.debtor_name || "—"}</Field>
                <Field label="Factuurdatum">{fmtDate(f.invoice_date)}</Field>
                <Field label="Vervaldatum">{fmtDate(f.due_date)}</Field>
                <Field label="Origineel factuurbedrag (te ontvangen)" testid="factoring-detail-amount">{fmtEur(f.amount)}</Field>
                <Field label="Fee">{f.interest_rate}%</Field>
                <Field label="Uit te keren aan klant"><span className="text-emerald-700">{fmtEur(f.payout)}</span></Field>
                <Field label="IBAN">{f.iban || "—"}</Field>
                <Field label="Uitbetaaldatum">{f.payout_date ? fmtDate(f.payout_date) : "—"}</Field>
                <Field label="Opvolgdatum">{f.followup_date ? fmtDate(f.followup_date) : "—"}</Field>
              </div>

              {(f.notes || f.comments) && (
                <div className="space-y-2">
                  {f.notes && <div><div className="text-xs text-slate-400">Notities</div><p className="text-sm text-slate-600 whitespace-pre-wrap">{f.notes}</p></div>}
                  {f.comments && <div><div className="text-xs text-slate-400">Opmerkingen</div><p className="text-sm text-slate-600 whitespace-pre-wrap">{f.comments}</p></div>}
                </div>
              )}

              {f.file_url && (
                <div>
                  <div className="text-xs text-slate-400 mb-1">Kopie-factuur</div>
                  <a href={photoSrc(f.file_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-emerald-700 hover:text-emerald-800 text-sm font-medium" data-testid="factoring-detail-file">
                    <FileText className="h-4 w-4" /> Bekijk factuur <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              )}

              <div className="rounded-xl border p-4 bg-slate-50 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs text-slate-400">Te ontvangen (factuurbedrag)</div>
                    <div className="font-bold text-slate-800 text-lg">{fmtEur(f.amount)}</div>
                  </div>
                  {f.collected ? (
                    <Button variant="outline" className="text-slate-600" disabled={collecting} onClick={toggleCollected} data-testid="factoring-collected-undo">
                      <RotateCcw className="h-4 w-4 mr-1.5" /> {collecting ? "Bezig…" : "Ontvangst terugzetten"}
                    </Button>
                  ) : (
                    <Button className="bg-blue-900 hover:bg-blue-800" disabled={collecting} onClick={toggleCollected} data-testid="factoring-collected-done">
                      <CheckCircle2 className="h-4 w-4 mr-1.5" /> {collecting ? "Bezig…" : "Factuurbedrag ontvangen"}
                    </Button>
                  )}
                </div>
                <div className="flex items-center justify-between gap-3 border-t pt-3">
                  <div>
                    <div className="text-xs text-slate-400">Uit te keren aan klant</div>
                    <div className="font-bold text-emerald-700 text-lg">{fmtEur(f.payout)}</div>
                  </div>
                  {f.payout_done ? (
                    <Button variant="outline" className="text-slate-600" disabled={toggling} onClick={togglePayout} data-testid="factoring-payout-undo">
                      <RotateCcw className="h-4 w-4 mr-1.5" /> {toggling ? "Bezig…" : "Uitbetaling terugzetten"}
                    </Button>
                  ) : (
                    <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={toggling} onClick={togglePayout} data-testid="factoring-payout-done">
                      <CheckCircle2 className="h-4 w-4 mr-1.5" /> {toggling ? "Bezig…" : "Uitbetaling afvinken"}
                    </Button>
                  )}
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1"><History className="h-3.5 w-3.5" /> Audittrail</div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {(f.audit || []).slice().reverse().map((a, i) => (
                    <div key={i} className="text-xs text-slate-500" data-testid={`factoring-detail-audit-${i}`}>{fmtDate(a.at)} — {a.action}{a.by ? ` (${a.by})` : ""}</div>
                  ))}
                  {(f.audit || []).length === 0 && <div className="text-xs text-slate-400">Geen registraties.</div>}
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Factuurnummer</Label><Input value={form.invoice_number || ""} onChange={(e) => set("invoice_number", e.target.value)} data-testid="factoring-edit-number" /></div>
                <div><Label>Debiteur / klant</Label><Input value={form.debtor_name || ""} onChange={(e) => set("debtor_name", e.target.value)} data-testid="factoring-edit-debtor" /></div>
                <div><Label>Factuurdatum</Label><DateField testId="factoring-edit-invdate" value={(form.invoice_date || "").slice(0, 10)} onChange={(v) => set("invoice_date", v)} /></div>
                <div><Label>Vervaldatum</Label><DateField testId="factoring-edit-duedate" value={(form.due_date || "").slice(0, 10)} onChange={(v) => set("due_date", v)} /></div>
                <div><Label>Bedrag (€)</Label><Input type="number" step="0.01" value={form.amount ?? ""} onChange={(e) => set("amount", e.target.value)} data-testid="factoring-edit-amount" /></div>
                <div><Label>Fee (%)</Label><Input type="number" step="0.01" value={form.interest_rate ?? ""} onChange={(e) => set("interest_rate", e.target.value)} data-testid="factoring-edit-rate" /></div>
                <div><Label>IBAN</Label><Input value={form.iban || ""} onChange={(e) => set("iban", e.target.value)} data-testid="factoring-edit-iban" /></div>
                <div><Label>Opvolgdatum</Label><DateField testId="factoring-edit-followup" value={(form.followup_date || "").slice(0, 10)} onChange={(v) => set("followup_date", v)} /></div>
                <div>
                  <Label>Status</Label>
                  <Combobox testId="factoring-edit-status" value={form.status || "open"} onChange={(v) => set("status", v)} options={[{ value: "open", label: "Openstaand" }, { value: "late", label: "Te laat" }, { value: "paid", label: "Betaald/Uitbetaald" }]} />
                </div>
              </div>
              <div><Label>Opmerkingen</Label><Textarea rows={2} value={form.comments || ""} onChange={(e) => set("comments", e.target.value)} data-testid="factoring-edit-comments" /></div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 grid grid-cols-3 gap-3 text-center">
                <div><div className="text-xs text-slate-500">Factuurbedrag</div><div className="font-bold text-slate-800">{fmtEur(editAmount)}</div></div>
                <div><div className="text-xs text-slate-500">Fee ({editRate}%)</div><div className="font-bold text-red-600">-{fmtEur(editAmount * editRate / 100)}</div></div>
                <div><div className="text-xs text-slate-500">Uit te betalen</div><div className="font-bold text-emerald-700" data-testid="factoring-edit-payout-preview">{fmtEur(editPayout)}</div></div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setEdit(false); setForm(f); }} data-testid="factoring-edit-cancel"><X className="h-4 w-4 mr-1.5" /> Annuleren</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={saving} onClick={save} data-testid="factoring-edit-save"><Check className="h-4 w-4 mr-1.5" /> {saving ? "Opslaan…" : "Opslaan"}</Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
